import { QrcodeIcon } from "@/components/Icon"
import React, { useRef, useMemo, useState } from "react"
import { useOutsideAlerter } from "@/components/hooks/useOutsideAlerter"
import Box, { BoxProps } from "@/components/Box"
import Typography from "@/components/Typography"
import { m } from "framer-motion"
import usePreloadImage from "@/utils/hooks/usePreloadImg"
import { ArrowRight24x24 as ArrowRight } from "@/components/Icon"
import { OneLinkQrCode } from "@/utils/onelink/OneLinkQRCode"

const childVariants = {
  hidden: { opacity: 0, scale: 0 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, type: "tween", ease: "linear" },
  },
}

const commonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}

export const QRDownloadBtn = ({
  ctaText,
  isQRCodeOpen,
  qrCodeType = "mainApp",
  background = "transparent",
  qrCodeSize = 119,
  setQRCodeOpen,
  campaign,
  style,
  isShowOneLinkQR = false,
  ...restProps
}: {
  ctaText: string
  isQRCodeOpen: boolean
  qrCodeType?: string
  background?: string
  qrCodeSize?: number | string
  setQRCodeOpen: (v: boolean) => void
  style?: React.CSSProperties
  isShowOneLinkQR?: boolean
  campaign?:
    | "app"
    | "card"
    | "converter"
    | "earn"
    | "exchange"
    | "general"
    | "pay"
    | "prime"
    | "rewards"
    | "level-up"
    | "staking"
    | "strikes"
    | "bitcoin"
    | "cfd"
    | "cryptobasket"
    | "cdcbtc"
    | "general-white"
    | "stocks"
    | "onchain"
    | "predictions"
} & BoxProps) => {
  const wrapperRef = useRef(null)
  const [shouldShowOneLinkQR, setShouldShowOneLinkQR] =
    useState(isShowOneLinkQR)

  useOutsideAlerter(wrapperRef, setQRCodeOpen, `${qrCodeType}-qrcode-container`)

  const qRImageSrc = useMemo(() => {
    switch (campaign) {
      case "app":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-app.svg`
      case "card":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-card.svg`
      case "converter":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-converter.svg`
      case "earn":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-earn.svg`
      case "exchange":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-exchange.svg`
      case "general":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-general.svg`
      case "general-white":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-general-white.svg`
      case "pay":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-pay.svg`
      case "prime":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-prime.svg`
      case "rewards":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-rewards.svg`
      case "level-up":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-level-up.svg`
      case "staking":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-staking.svg`
      case "strikes":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-strikes.svg`
      case "stocks":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-stocks.svg`
      case "bitcoin":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-bitcoin.svg`
      case "cfd":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-cfd.svg`
      case "cryptobasket":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-cryptobasket.svg`
      case "onchain":
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-onchain.svg`
      case "predictions":
        return `${process.env.ASSETS_BASE_URL}/predictions/qr_code.svg`
      default:
        return `${process.env.ASSETS_BASE_URL}/qr-code/qr-code-page-general.svg`
    }
  }, [campaign])

  usePreloadImage(qRImageSrc)

  const qrCodeStyle = {
    ...restProps,
    ...commonStyle,
    background,
    borderRadius: 12,
    height: "auto",
    ...style,
  }

  const buttonStyle = {
    ...restProps,
    ...commonStyle,
    borderRadius: 40,
    background,
    height: restProps.height || "44px",
    cursor: "pointer",
    padding: "10px 18px 10px 22px",
    width: restProps.width || "auto",
  }

  return (
    <Box
      className={`${qrCodeType}-qrcode-container`}
      as="button"
      bg="transparent"
      border="none"
      padding={0}
    >
      <m.div
        style={isQRCodeOpen ? qrCodeStyle : buttonStyle}
        onClick={() => setQRCodeOpen(true)}
      >
        {!isQRCodeOpen ? (
          <>
            <Typography
              variant="l2"
              fontSize={18}
              lineHeight={"110%"}
              fontWeight="600"
              className="smoothed"
            >
              {ctaText}
            </Typography>
            {campaign === "predictions" ? (
              <ArrowRight ml={"8px"} fontSize={"24px"} />
            ) : (
              <QrcodeIcon fontSize={24} ml={"8px"} />
            )}
          </>
        ) : (
          <m.div
            variants={childVariants}
            initial="hidden"
            animate="visible"
            ref={wrapperRef}
            style={{ width: qrCodeSize, display: "flex", height: qrCodeSize }}
          >
            {shouldShowOneLinkQR ? (
              <OneLinkQrCode
                onRenderResult={result => {
                  if (result === "failure") {
                    // fallback to static QR image
                    setShouldShowOneLinkQR(false)
                  }
                }}
              />
            ) : (
              <img
                src={qRImageSrc}
                style={{ height: "100%", width: "100%", borderRadius: 12 }}
                alt="qr-code"
              />
            )}
          </m.div>
        )}
      </m.div>
    </Box>
  )
}
